// 웹 푸시(FCM) 알림 허용·토큰 등록
// firebase 는 동적 import 로만 불러 처음 등록할 때 내려받는다(콘솔 번들에 들어가지 않게)
// 알림 표시·클릭 이동은 public/firebase-messaging-sw.js 가 한다
import Vue from "vue";
import plainAxios from "axios";
import store from "@/store";
import { getSavedToken, saveToken, removeSavedToken, isSynced, markSynced, clearSynced, setPendingRegistration, releaseSaved, release, isOptedOut, setOptedOut } from "./push-token";

export { release, clearSynced, TOKEN_KEY } from "./push-token";

const SW_URL = "/firebase-messaging-sw.js";
const REGISTER_URL = "/api/v1/me/push-tokens";

// 다시 등록할 때 잠시 기다리는 시간. 다른 곳(다른 탭·이 탭)이 보낸 해제 요청이 서버에 먼저 닿게 한다
export const RESYNC_DELAY = 1500;

// 회원 scope. 백엔드 로그인이 user.scope 를 JWT 에 넣고(auth.controller.js), 회원 기본값은 ["member"](models/user/users/member.js).
// 관리자 등 다른 계정이 같은 탭에서 사이트를 열어도 그 계정으로는 등록하지 않는다
const MEMBER_SCOPE = "member";

// 안내 문구. 사용자 확인 대기 중이라 바뀔 수 있어 여기 한 곳에 모은다
export const PUSH_TEXTS = {
    banner: "캐시 선물이 도착하면 알림으로 알려드릴까요?",
    button: "선물 알림 받기",
    offButton: "선물 알림 받기 취소",
    offConfirm: "이 기기에서 캐시 선물 알림을 받지 않으시겠습니까?",
    off: "이 기기에서 캐시 선물 알림을 받지 않습니다.",
    granted: "캐시 선물이 도착하면 알림으로 알려드립니다.",
    denied: "알림이 차단되어 있습니다. 브라우저 설정에서 알림을 허용해 주세요.",
    dismissed: "알림 허용을 선택하지 않았습니다.",
    failed: "알림 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.",
};

// 값이 없으면 쓰는 순간 에러. 실제로 토큰을 받는 register() 안에서만 부른다
function config() {
    const values = {
        VUE_APP_FIREBASE_API_KEY: process.env.VUE_APP_FIREBASE_API_KEY,
        VUE_APP_FIREBASE_AUTH_DOMAIN: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
        VUE_APP_FIREBASE_PROJECT_ID: process.env.VUE_APP_FIREBASE_PROJECT_ID,
        VUE_APP_FIREBASE_STORAGE_BUCKET: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
        VUE_APP_FIREBASE_MESSAGING_SENDER_ID: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
        VUE_APP_FIREBASE_APP_ID: process.env.VUE_APP_FIREBASE_APP_ID,
        VUE_APP_FIREBASE_VAPID_KEY: process.env.VUE_APP_FIREBASE_VAPID_KEY,
    };
    const missing = Object.keys(values).filter((key) => !values[key]);
    if (missing.length) throw new Error(`[push] 환경 변수가 없습니다: ${missing.join(", ")}`);

    return {
        firebaseConfig: {
            apiKey: values.VUE_APP_FIREBASE_API_KEY,
            authDomain: values.VUE_APP_FIREBASE_AUTH_DOMAIN,
            projectId: values.VUE_APP_FIREBASE_PROJECT_ID,
            storageBucket: values.VUE_APP_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: values.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
            appId: values.VUE_APP_FIREBASE_APP_ID,
        },
        vapidKey: values.VUE_APP_FIREBASE_VAPID_KEY,
    };
}

// 보안 출처(https·localhost) + 서비스 워커 + 푸시 + 알림. 지원 여부만 본다(env 는 보지 않는다)
export function isSupported() {
    return !!window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

// "default" | "granted" | "denied"
export function permission() {
    return "Notification" in window ? Notification.permission : "denied";
}

// 띠와 받은 선물 목록 버튼이 같이 읽는 권한 상태. 한쪽에서 허용·거절하면 다른 쪽도 바로 사라진다.
// optedOut: 이 기기에서 "알림 받기 취소"를 눌렀는지(push-token.js isOptedOut)
export const pushState = Vue.observable({ permission: permission(), optedOut: isOptedOut() });

function isMember() {
    const scope = store.state.scope;
    return Array.isArray(scope) && scope.includes(MEMBER_SCOPE);
}

// 서버 등록. 401·403 에 alert 하는 @/plugins/axios 를 쓰지 않는다(오류 창은 버튼 쪽에서 한 번만).
// accessToken(5분)이 만료돼 401 이면 한 번 갱신하고 다시 보낸다. 갱신 실패는 그대로 던진다(로그아웃은 하지 않는다)
async function post(token) {
    const send = () => plainAxios.post(REGISTER_URL, { token }, { headers: { Authorization: `Bearer ${store.state.accessToken}` } });
    try {
        await send();
    } catch (error) {
        if (error?.response?.status != 401 || !store.state.refreshToken) throw error;
        await store.dispatch("refresh", { refreshToken: store.state.refreshToken, _cert: "" });
        await send();
    }
}

// 서비스 워커 등록 → 토큰 받기 → 서버 등록 → 이 브라우저에 저장 → 이 탭에 재등록 표시
async function registerOnce() {
    const { firebaseConfig, vapidKey } = config();
    const [{ initializeApp, getApps }, { getMessaging, getToken }] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
    const app = getApps()[0] || initializeApp(firebaseConfig);

    await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;

    // getToken 은 권한이 default 면 스스로 권한을 묻는다. 그래서 granted 확인 뒤에만 부른다
    const token = await getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration });
    if (!token) throw new Error("[push] 토큰을 받지 못했습니다");

    await post(token);
    return token;
}

// 등록 도중 "다시 등록해야 함"(다른 탭의 해제·비밀번호 변경)을 받은 횟수. 메모리에만 둔다.
// 등록이 시작될 때 값을 기억했다가 끝날 때 달라졌으면, 서버가 이 등록보다 해제를 늦게 처리해 토큰을 지웠을 수 있다.
// 그때는 재등록 표시를 찍지 않고 잠시 뒤 한 번 더 등록한다
let resyncRequested = 0;

// 다시 등록해야 함을 알린다. 이 탭의 재등록 표시를 지우고, 진행 중인 등록이 있으면 끝난 뒤 한 번 더 등록하게 한다
export function requestResync() {
    resyncRequested++;
    clearSynced();
}

// 진행 중인 등록을 push-token.js 에 알려 로그아웃(release)이 끝나기를 기다리게 한다.
// 등록 도중 로그아웃·다른 계정 로그인으로 세션(refreshToken)이 바뀌었으면 방금 등록한 토큰을 지운다.
// 저장했다 지우므로 다른 탭에 storage 이벤트가 가고, 로그인된 탭은 다시 등록한다
function register() {
    const session = store.state.refreshToken;
    const requested = resyncRequested;
    const promise = registerOnce().then((token) => {
        saveToken(token);
        if (store.state.refreshToken != session) {
            releaseSaved();
            const error = new Error("[push] 등록 중에 로그인 상태가 바뀌어 토큰을 해제했습니다");
            error.stale = true;
            throw error;
        }
        // 저장(saveToken)은 값이 있는 storage 이벤트라 다른 탭은 무시한다(AppMain.onStorage). 그래서 이 재등록은 되풀이되지 않는다
        if (resyncRequested != requested) setTimeout(sync, RESYNC_DELAY);
        else markSynced();
        return token;
    });
    setPendingRegistration(promise);
    return promise;
}

// 버튼 클릭에서 부른다. 사용자 동작 안에서 권한을 물어야 하므로 requestPermission 을 첫 await 보다 먼저 호출한다.
// 반환: 권한 결과("granted" | "denied" | "default"). 등록 실패는 던진다
export async function enable() {
    const result = await Notification.requestPermission();
    pushState.permission = result;
    if (result != "granted") return result;

    setOptedOut(false);
    pushState.optedOut = false;

    await register();
    return result;
}

// 버튼 클릭 처리 + 결과 안내. 띠와 받은 선물 목록 버튼이 같이 쓴다. 반환: 권한 결과.
// notify 를 주지 않으면 alert(클라이언트 관례). 받은 선물 목록은 화면 안 확인창을 넘긴다(아이폰 홈 화면 앱에서 alert 가 막힘)
export async function enableWithAlert(notify = (message) => alert(message)) {
    try {
        const result = await enable();
        switch (result) {
            case "granted":
                await notify(PUSH_TEXTS.granted);
                break;
            case "denied":
                await notify(PUSH_TEXTS.denied);
                break;
            default:
                await notify(PUSH_TEXTS.dismissed);
        }
        return result;
    } catch (error) {
        console.error("[push] 알림 등록 실패", error);
        await notify(PUSH_TEXTS.failed);
        pushState.permission = permission();
        return pushState.permission;
    }
}

// 이미 허용된 브라우저에서 회원이 로그인돼 있으면 탭당 1회, 권한을 묻지 않고 토큰을 다시 등록한다.
// 표시가 있어도 저장 토큰이 없으면(다른 탭의 로그아웃이 지웠으면) 다시 등록한다.
// 표시는 등록이 성공한 뒤에 찍는다. 로그인 직후 페이지 이동(Login.vue 의 $router.go(-1))으로 등록이 끊기면
// 다음 페이지에서 다시 시도해야 하기 때문이다. 진행 중에 겹쳐 불리는 것은 syncing 으로만 막는다.
// 무슨 일이 있어도 promise 를 돌려주고 reject 하지 않는다. 실패는 console.error 만 남긴다(자동이라 오류 창을 띄우지 않는다)
let syncing = null;

export function sync() {
    try {
        if (!isMember()) return Promise.resolve();
        if (isOptedOut()) return Promise.resolve();
        if (isSynced() && getSavedToken()) return Promise.resolve();
        if (!isSupported() || permission() != "granted") return Promise.resolve();

        if (!syncing) {
            let stale = false;
            syncing = register()
                .then(() => {})
                .catch((error) => {
                    stale = !!error?.stale;
                    console.error("[push] 토큰 재등록 실패", error);
                })
                .finally(() => {
                    syncing = null;
                    // 등록 중에 다른 회원이 이 탭에 로그인했으면 그 회원으로 다시 등록한다(로그아웃 상태면 isMember 에서 멈춘다).
                    // 방금 보낸 해제(releaseSaved)가 서버에 먼저 닿도록 잠시 뒤에 한다
                    if (stale) setTimeout(sync, RESYNC_DELAY);
                });
        }
        return syncing;
    } catch (error) {
        console.error("[push] 토큰 재등록 실패", error);
        return Promise.resolve();
    }
}

// 받은 선물 목록의 "알림 받기 취소". 이 기기 토큰을 서버에서 지우고 다시 등록하지 않게 표시한다.
// 표시를 먼저 남긴다: 저장 토큰을 지우면 로그인된 다른 탭이 storage 이벤트로 sync 를 부르는데, 그때 표시를 보고 멈추게 하려는 것이다.
// 진행 중인 등록이 있으면 release 가 끝나기를 기다렸다 지운다. 서버 해제는 응답을 기다리지 않는다(로그아웃과 같다)
export async function disable() {
    setOptedOut(true);
    pushState.optedOut = true;
    await release();
}

// 마이페이지에서 비밀번호를 바꾼 저장이 성공한 뒤 부른다. 서버가 이 회원의 푸시 토큰을 전부 지웠으므로(me.controller.js)
// 해제 요청은 보내지 않고 이 브라우저의 저장 토큰과 이 탭의 재등록 표시만 지운 뒤 다시 등록한다.
// 저장 토큰을 지우면 다른 탭도 storage 이벤트로 다시 등록한다. 탭마다 다른 회원이 로그인해 있으면
// 서버가 같은 토큰을 마지막에 등록한 회원으로 바꿔 달기 때문에(push-tokens.controller.js) 그 회원이 갖게 된다
export function resyncAfterPasswordChange() {
    removeSavedToken();
    requestResync();
    return sync();
}
